import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useVirtualWindow } from "../../lib/useVirtualWindow";
import { Alert, withPlaceholder } from "../../common";
import InfiniteListPlaceholder from "./InfiniteListPlaceholder";
import { StreamCol, StreamRow } from "./InfiniteList.style";

const InfiniteList = (props) => {
    const { query = {}, name = 'list', renderItem = () => { }, noContent, children = null } = props
    const { t } = useTranslation();
    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        status,
    } = query;

    const list = data ? data.pages.flatMap((page) => page?.[name]) : [];
    const parentRef = useRef();
    const windowRef = useRef(window);

    const rowVirtualizer = useVirtualWindow({
        estimateSize: useCallback(() => 450, []),
        overscan: 5,
        paddingStart: 0,
        parentRef,
        size: list.length,
        windowRef: windowRef,
    });

    useEffect(() => {
        const [lastItem] = [...rowVirtualizer.virtualItems].reverse();

        if (!lastItem) {
            return;
        }

        if (
            lastItem.index === list.length - 1 &&
            hasNextPage &&
            !isFetchingNextPage
        ) {
            fetchNextPage();
        }
    }, [
        hasNextPage,
        fetchNextPage,
        list.length,
        isFetchingNextPage,
        rowVirtualizer.virtualItems,
    ]);

    if (status === "error") {
        throw error;
    }

    if (status === "loading") {
        return <InfiniteListPlaceholder />;
    }

    const emptyList = typeof noContent === 'undefined'
        ? <Alert color="warning">{t('There are no list to show.')}</Alert>
        : noContent

    return (
        <>
            { children }
            <StreamCol
                ref={parentRef}
                totalSize={rowVirtualizer.totalSize}
            >
                {
                    rowVirtualizer.virtualItems.length
                        ? (
                            rowVirtualizer.virtualItems.map((virtualRow) => {
                                return (
                                    <StreamRow
                                        key={virtualRow.index}
                                        ref={virtualRow.measureRef}
                                        start={virtualRow.start}
                                        zIndex={list.length - virtualRow.index}
                                    >
                                        {
                                            renderItem({ ...list[virtualRow.index] })
                                        }
                                    </StreamRow>
                                );
                            })
                        )
                        : emptyList
                }
            </StreamCol>
            { isFetchingNextPage ? <div className="mt-2"><InfiniteListPlaceholder /></div> : null }
        </>
    );
};

export default withPlaceholder(InfiniteList, InfiniteListPlaceholder);
